import React from "react";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FormatSizeRoundedIcon from "@mui/icons-material/FormatSizeRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

const TOOLS = [
  { id: "content", label: "Textos", icon: TextFieldsRoundedIcon },
  { id: "colors", label: "Colores", icon: PaletteRoundedIcon },
  { id: "style", label: "Estilo", icon: TuneRoundedIcon },
  { id: "type", label: "Tipografía", icon: FormatSizeRoundedIcon },
  { id: "image", label: "Imágenes", icon: ImageRoundedIcon },
];

export default function LandingSegmentToolbar({
  sectionId,
  label,
  enabled,
  canToggle,
  device = "desktop",
  contextual = false,
  onToggle,
  onOpenTool,
}) {
  const compactViewport = useMediaQuery("(max-width:1100px)");
  const touchInput = useMediaQuery("(hover: none), (pointer: coarse)");
  const compact = device !== "desktop" || compactViewport || touchInput;

  return (
    <Box
      className="landing-segment-toolbar"
      onClick={(event) => event.stopPropagation()}
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: compact ? "flex-start" : "flex-end",
        px: compact ? 0.75 : 1,
        py: 0.65,
        bgcolor: "rgba(244,245,247,.96)",
        borderBottom: "1px solid rgba(25,36,47,.08)",
        pointerEvents: "auto",
      }}
    >
      <Stack
        direction="row"
        spacing={0.45}
        useFlexGap
        flexWrap={compact ? "wrap" : "nowrap"}
        alignItems="center"
        sx={{
          width: compact ? "100%" : "auto",
          maxWidth: "100%",
          p: 0.55,
          borderRadius: compact ? 2.5 : 999,
          bgcolor: "rgba(25,36,47,.94)",
          color: "#fff",
          boxShadow: "0 12px 36px rgba(25,36,47,.28)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            pl: 1,
            pr: 0.65,
            maxWidth: compact ? "100%" : 190,
            flex: compact ? "1 0 100%" : "0 0 auto",
            fontWeight: 950,
          }}
        >
          Editando · {label}
        </Typography>

        {contextual ? (
          <Button
            size="small"
            startIcon={<TuneRoundedIcon fontSize="small" />}
            aria-label={`Ajustes de sección de ${label}`}
            onClick={() => onOpenTool(sectionId, "section")}
            sx={{
              minWidth: 0,
              minHeight: compact ? 44 : undefined,
              flex: compact ? "1 1 120px" : undefined,
              px: 1.05,
              color: "#fff",
              bgcolor: "rgba(255,255,255,.08)",
              borderRadius: compact ? 2 : 999,
              textTransform: "none",
              fontWeight: 850,
              "&:hover": { bgcolor: "rgba(255,255,255,.14)" },
            }}
          >
            Editar sección
          </Button>
        ) : TOOLS.map(({ id, label: toolLabel, icon: Icon }) =>
          compact ? (
            <Button
              key={id}
              size="small"
              startIcon={<Icon fontSize="small" />}
              aria-label={`${toolLabel} de ${label}`}
              onClick={() => onOpenTool(sectionId, id)}
              sx={{
                minWidth: 0,
                minHeight: 44,
                flex: "1 1 92px",
                px: 1,
                color: "#fff",
                bgcolor: "rgba(255,255,255,.08)",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 850,
                "& .MuiButton-startIcon": { mr: 0.55 },
              }}
            >
              {toolLabel}
            </Button>
          ) : (
            <Button
              key={id}
              size="small"
              startIcon={<Icon fontSize="small" />}
              onClick={() => onOpenTool(sectionId, id)}
              sx={{
                minWidth: 0,
                px: 1.05,
                color: "#fff",
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 850,
                "&:hover": { bgcolor: "rgba(255,255,255,.12)" },
              }}
            >
              {toolLabel}
            </Button>
          )
        )}

        {canToggle ? (
          compact ? (
            <Button
              size="small"
              startIcon={enabled ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
              aria-label={enabled ? "Ocultar sección" : "Mostrar sección"}
              onClick={() => onToggle(!enabled)}
              sx={{
                minWidth: 0,
                minHeight: 44,
                flex: "1 1 112px",
                px: 1,
                color: enabled ? "#fff" : "#F9B4A6",
                bgcolor: "rgba(255,255,255,.08)",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 850,
                "& .MuiButton-startIcon": { mr: 0.55 },
              }}
            >
              {enabled ? "Ocultar" : "Mostrar"}
            </Button>
          ) : (
            <Button
              size="small"
              startIcon={
                enabled ? (
                  <VisibilityOffRoundedIcon fontSize="small" />
                ) : (
                  <VisibilityRoundedIcon fontSize="small" />
                )
              }
              onClick={() => onToggle(!enabled)}
              sx={{
                minWidth: 0,
                px: 1.05,
                flexShrink: 0,
                color: enabled ? "#fff" : "#F9B4A6",
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,.08)",
                textTransform: "none",
                fontWeight: 850,
                "&:hover": { bgcolor: "rgba(255,255,255,.14)" },
              }}
            >
              {enabled ? "Ocultar" : "Mostrar"}
            </Button>
          )
        ) : null}
      </Stack>
    </Box>
  );
}
